<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="menu-loading-screen">
      <img src="@/assets/logo-1x.png" alt="Logo" class="menu-loading-logo">
      <div class="menu-loading-spinner"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <v-container class="fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="6" class="text-center">
            <v-icon size="120" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <h2 class="mt-4 mb-2">{{ error }}</h2>
            <p class="text-body-1 grey--text">Molimo kontaktirajte osoblje za pomoć.</p>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Dynamic Template Component -->
    <component
      v-else-if="menuData"
      :is="currentTemplateComponent"
      :menu-data="menuData"
    ></component>
  </div>
</template>

<script>
import PublicMenuAikasa from './templates/PublicMenuAikasa.vue'
import PublicMenuModern from './templates/PublicMenuModern.vue'

export default {
  name: 'PublicMenuWrapper',
  components: {
    PublicMenuAikasa,
    PublicMenuModern
  },
  data () {
    return {
      locationId: null,
      menuData: null,
      cachedMenuData: null,
      cacheTimestamp: null,
      CACHE_DURATION: 5 * 60 * 1000, // 5 minutes in milliseconds
      loading: true,
      error: null
    }
  },
  computed: {
    selectedTemplate () {
      return this.menuData?.location?.menuSettings?.selected_template || 'aikasa'
    },
    currentTemplateComponent () {
      const templateMap = {
        aikasa: 'PublicMenuAikasa',
        modern: 'PublicMenuModern'
      }
      return templateMap[this.selectedTemplate] || 'PublicMenuAikasa'
    }
  },
  async mounted () {
    this.locationId = this.$route.params.locationId
    await this.loadMenuData()
  },
  methods: {
    async loadMenuData () {
      this.loading = true
      this.error = null

      try {
        console.log('[PublicMenuWrapper] Starting to load menu data for locationId:', this.locationId)

        // Validate locationId
        if (!this.locationId) {
          console.error('[PublicMenuWrapper] No locationId provided')
          this.error = 'ID lokacije nije pronađen'
          this.loading = false
          return
        }

        // Check if we have valid cached data
        const now = Date.now()
        if (this.cachedMenuData && this.cacheTimestamp && (now - this.cacheTimestamp < this.CACHE_DURATION)) {
          console.log('[PublicMenuWrapper] Using cached data (age: ' + Math.round((now - this.cacheTimestamp) / 1000) + 's)')
          this.menuData = this.cachedMenuData
          this.loading = false
          return
        }

        // Call Cloud Function to get menu data
        // Use the deployed Cloud Function URL
        const projectId = process.env.VUE_APP_FIREBASE_PROJECT_ID || 'ai-fisk-stage'
        const functionUrl = `https://europe-west1-${projectId}.cloudfunctions.net/getPublicMenu`
        console.log('[PublicMenuWrapper] Calling Cloud Function:', functionUrl)

        const startTime = performance.now()
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            locationId: this.locationId
          })
        })
        const endTime = performance.now()
        console.log('[PublicMenuWrapper] API call took:', Math.round(endTime - startTime), 'ms')

        if (!response.ok) {
          const errorData = await response.json()
          console.error('[PublicMenuWrapper] Cloud Function error:', errorData)

          if (response.status === 404) {
            this.error = 'Prodajno mjesto nije pronađeno'
          } else if (response.status === 403) {
            this.error = 'QR meni nije omogućen za ovu lokaciju'
          } else {
            this.error = errorData.error || 'Greška pri učitavanju menija'
          }
          this.loading = false
          return
        }

        const data = await response.json()
        console.log('[PublicMenuWrapper] Menu data loaded from Cloud Function')

        if (!data.success) {
          this.error = 'Greška pri učitavanju menija'
          this.loading = false
          return
        }

        // Cache the data
        this.cachedMenuData = data
        this.cacheTimestamp = Date.now()

        // Set menu data
        this.menuData = data

        console.log('[PublicMenuWrapper] Menu data loaded successfully. Template:', this.selectedTemplate)
        this.loading = false
      } catch (error) {
        console.error('[PublicMenuWrapper] Error loading menu:', error)

        // Provide more specific error messages
        if (error.code === 'permission-denied') {
          this.error = 'Nemate pristup ovom meniju'
        } else if (error.code === 'unavailable') {
          this.error = 'Usluga trenutno nije dostupna'
        } else if (error.message && error.message.includes('network')) {
          this.error = 'Greška u mrežnoj vezi'
        } else {
          this.error = 'Greška pri učitavanju menija'
        }
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Loading Screen Styles */
.menu-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.menu-loading-logo {
  width: 200px;
  height: auto;
  margin-bottom: 40px;
  animation: menu-logo-pulse 2s ease-in-out infinite;
}

@keyframes menu-logo-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
}

.menu-loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #00b3be;
  border-radius: 50%;
  animation: menu-spinner-spin 1s linear infinite;
}

@keyframes menu-spinner-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}
</style>
